import React, { FunctionComponent, ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import { LoadingSpinner } from "@/components/Loading";
import { Text } from "@/components/Text";
import { Flex, IFlexProps } from "@/eworkitComponents";
import { useEditMode } from "@/hooks/EditMode";

import { IEditableSectionProps } from "./types";

interface IEditableSectionComponentProps extends IFlexProps {
  children: (editableProps: IEditableSectionProps) => ReactElement;
  editable?: boolean;
  onSaveClick?: () => void;
  onCancelClick?: () => void;
  hasCancelButton?: boolean;
  onEditClick?: () => void;
}

export const EditableSection: FunctionComponent<IEditableSectionComponentProps> = ({
  children,
  editable = true,
  onEditClick,
  onSaveClick,
  onCancelClick,
  hasCancelButton = true,
  ...props
}) => {
  const editMode = useEditMode();
  const [saveClicked, setSaveClicked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);

  const { t } = useTranslation("session");

  const handleEditClick = () => {
    if (onEditClick) {
      onEditClick();
    }
    editMode.handleEditClick();
  };

  const handleSaveClick = () => {
    if (onSaveClick) {
      onSaveClick();
      editMode.handleCancelClick();
    } else {
      setSaveClicked(true);
    }
  };

  const handleCancelSubmit = () => {
    setSaveClicked(false);
    if (onCancelClick) {
      onCancelClick();
    }
  };

  const handleCancelClick = () => {
    handleCancelSubmit();
    setSubmitting(false);
    editMode.handleCancelClick();
  };

  return (
    <Flex grow={1} direction="column" position="relative" {...props}>
      {editable && (
        <Flex position="absolute" side="right">
          {editMode.value ? (
            <Flex position="relative" innerMargin="sm">
              {hasCancelButton && (
                <Button
                  variant="secondary"
                  onClick={handleCancelClick}
                  testName="cancel"
                  disabled={disabled || submitting}>
                  {t("COMPONENTS.EDITABLE_SECTION.BUTTON_CANCEL")}
                </Button>
              )}
              <Button testName="save" onClick={handleSaveClick} disabled={disabled || submitting || saveDisabled}>
                {submitting ? (
                  <Flex direction="row" innerMargin="sm" align="center" justify="center">
                    <LoadingSpinner />
                    <Text>{t("COMPONENTS.EDITABLE_SECTION.SAVING")}</Text>
                  </Flex>
                ) : (
                  t("COMPONENTS.EDITABLE_SECTION.BUTTON_SAVE")
                )}
              </Button>
            </Flex>
          ) : (
            <Button variant="clean" onClick={handleEditClick}>
              <Icon color="grey2" icon="edit" size="lg" />
            </Button>
          )}
        </Flex>
      )}
      {children({
        editMode: editMode.value,
        setEditMode: editMode.handleEditClick,
        submitting,
        setSubmitting,
        setDisabled,
        saveClicked,
        setSaveClicked,
        setSaveDisabled,
        handleCancelClick,
        handleCancelSubmit,
        editable,
      })}
    </Flex>
  );
};
